import {FC, useState} from 'react'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'

interface LoginCardProps {
  onSubmit: (values: {username: string; password: string}) => Promise<string | null>
  loading: boolean
}

export const LoginCard: FC<LoginCardProps> = ({onSubmit, loading}) => {
  const intl = useIntl()
  const [showPassword, setShowPassword] = useState(false)
  const [status, setStatus] = useState<string | null>(null)

  const schema = Yup.object().shape({
    username: Yup.string()
      .email(intl.formatMessage({id: 'FORM.ERROR.EMAIL.WRONG_FORMAT'}))
      .required(intl.formatMessage({id: 'FORM.ERROR.REQUIRED'})),
    password: Yup.string().required(intl.formatMessage({id: 'FORM.ERROR.REQUIRED'})),
  })

  const formik = useFormik({
    initialValues: {username: '', password: ''},
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      helpers.setSubmitting(true)
      setStatus(null)
      const error = await onSubmit(values)
      if (error) {
        setStatus(error)
        helpers.setSubmitting(false)
      }
    },
  })

  const submitting = loading || formik.isSubmitting

  const hasError = Boolean(status)

  return (
    <div
      className={'marefati-login' + (hasError ? ' marefati-login--error' : '')}
      role='region'
      aria-label={intl.formatMessage({id: 'AUTH.LOGIN.TITLE'}, {defaultMessage: 'Connexion'})}
    >
      <div className='marefati-login__header'>
        <div className='marefati-login__secure'>
          <span className='marefati-login__lock' aria-hidden>
            <i className='bi bi-shield-lock-fill' />
          </span>
          <span className='marefati-login__secure-text'>Espace sécurisé Maarefati</span>
        </div>
      </div>

      <div className='marefati-login__title'>
        <h1 id='marefati-login-title'>Connexion</h1>
      </div>

      <form
        className='marefati-login__form'
        onSubmit={formik.handleSubmit}
        noValidate
        aria-labelledby='marefati-login-title'
        aria-describedby={hasError ? 'marefati-login-error' : undefined}
      >
        <div className='marefati-login__field'>
          <label className='marefati-login__label' htmlFor='marefati-login-email'>
            Email institutionnel
          </label>
          <input
            id='marefati-login-email'
            type='email'
            autoComplete='email'
            {...formik.getFieldProps('username')}
            className={
              'marefati-login__input' +
              (formik.touched.username && formik.errors.username
                ? ' marefati-login__input--error'
                : '')
            }
            placeholder='prenom.nom@maarefati.com'
            aria-invalid={Boolean(formik.touched.username && formik.errors.username)}
            aria-errormessage={formik.touched.username && formik.errors.username ? 'marefati-email-error' : undefined}
          />
          {formik.touched.username && formik.errors.username && (
            <div id='marefati-email-error' className='marefati-login__error' role='alert'>
              {formik.errors.username}
            </div>
          )}
        </div>

        <div className='marefati-login__field'>
          <div className='marefati-login__label-row'>
            <label className='marefati-login__label' htmlFor='marefati-login-password'>
              Mot de passe
            </label>
            <Link to='/auth/forgot-password' className='marefati-login__link'>
              Mot de passe oublié ?
            </Link>
          </div>
          <div className='marefati-login__password-wrapper'>
            <input
              id='marefati-login-password'
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              {...formik.getFieldProps('password')}
              className={
                'marefati-login__input' +
                (formik.touched.password && formik.errors.password
                  ? ' marefati-login__input--error'
                  : '')
              }
              placeholder='********'
              aria-invalid={Boolean(formik.touched.password && formik.errors.password)}
              aria-errormessage={formik.touched.password && formik.errors.password ? 'marefati-password-error' : undefined}
            />
            <button
              type='button'
              className='marefati-login__password-toggle'
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              aria-pressed={showPassword}
            >
              <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} aria-hidden />
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div id='marefati-password-error' className='marefati-login__error' role='alert'>
              {formik.errors.password}
            </div>
          )}
        </div>

        <div className='marefati-login__options'>
          <label className='marefati-login__remember'>
            <input type='checkbox' aria-label='Se souvenir de moi' />
            <span>Se souvenir de moi</span>
          </label>
        </div>

        {status && (
          <div id='marefati-login-error' className='marefati-login__status' role='alert'>
            {status}
          </div>
        )}

        <button
          type='submit'
          disabled={submitting || !formik.isValid}
          className='marefati-login__submit'
          aria-busy={submitting}
        >
          {submitting && <span className='spinner-border spinner-border-sm me-2' aria-hidden />}
          Se connecter
        </button>

        <div className='marefati-login__signup'>
          <span className='marefati-login__signup-text'>
            {intl.formatMessage({id: 'MENU.NEW_HERE?'})}{' '}
          </span>
          <Link to='/auth/registration' className='marefati-login__signup-btn'>
            {intl.formatMessage({id: 'MENU.CREATE_AN_ACCOUNT'})}
          </Link>
        </div>

        <div className='marefati-login__meta'>
          <span className='marefati-login__version'>v1.1.0</span>
          <span className='marefati-login__copy'>
            © {new Date().getFullYear()} Maarefati
          </span>
        </div>
      </form>
    </div>
  )
}

export default LoginCard

